import Grid from '@mui/material/Grid';
import type { ContentObjectOption, OptionsConfig } from '@openx/types/targeting/contentObject.ts';
import type { UseContentObjectOptionsConfig } from '@openx/types/targeting/targetingHookTypes.ts';
import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';
import { DrawerTopBox } from '../../shared/TargetingDrawer/DrawerTopBox.tsx';
import type { ContentObjectOptionsProps, ContentObjectTopBoxProps } from '../types.ts';
import { isDropdown, isEpisode, isFreeText, isHierarchy, isOptionItem, isStatic } from '../utils/utils.ts';
import { EpisodeOptions } from './OptionDropdowns/Episode/EpisodeOptions.tsx';
import { FreeTextOptions } from './OptionDropdowns/FreeTextOptions.tsx';
import { HierarchyOptions } from './OptionDropdowns/Hierarchy/HierarchyOptions.tsx';
import { StandardDropdown } from './OptionDropdowns/StandardDropdown.tsx';
import { StaticOptions } from './OptionDropdowns/StaticOptions.tsx';
import { TypeOptionDropdown } from './OptionDropdowns/TypeOptionDropdown.tsx';

export const ContentObjectTopBox = (props: ContentObjectTopBoxProps) => {
  const { useContentObjectOptionsConfig } = useTargetingContext();

  if (!useContentObjectOptionsConfig) {
    throw new Error('useContentObjectOptionsConfig is not provided');
  }

  return <ContentObjectTopBoxRender {...props} useContentObjectOptionsConfig={useContentObjectOptionsConfig} />;
};

export const ContentObjectOptionsRender = ({
  type,
  optionsConfig,
  formState,
  dispatch,
}: ContentObjectOptionsProps & { optionsConfig: OptionsConfig } & { loading: boolean; options }) => {
  const { useOptionFetch, ...restOptionsConfig } = optionsConfig;

  const optionItem = restOptionsConfig[type];

  if (isDropdown(type))
    return <StandardDropdown type={type} useOptionFetch={useOptionFetch} formState={formState} dispatch={dispatch} />;

  if (isStatic(type) && isOptionItem(optionItem)) {
    const { loading = false, options } = optionItem;
    return <StaticOptions type={type} loading={loading} dispatch={dispatch} options={options} />;
  }

  if (isFreeText(type)) return <FreeTextOptions type={type} formState={formState} dispatch={dispatch} />;

  if (isEpisode(type)) return <EpisodeOptions type={type} formState={formState} dispatch={dispatch} />;

  if (isHierarchy(type))
    return <HierarchyOptions type={type} formState={formState} useOptionFetch={useOptionFetch} dispatch={dispatch} />;

  return null;
};

const getAlign = (type: ContentObjectOption) => {
  if (isDropdown(type)) return 'flex-end';
  if (isEpisode(type)) return 'flex-start';
};

const ContentObjectTopBoxRender = ({
  formState,
  dispatch,
  useContentObjectOptionsConfig,
  type,
  setType,
}: ContentObjectTopBoxProps & {
  useContentObjectOptionsConfig: UseContentObjectOptionsConfig;
}) => {
  const optionsConfig = useContentObjectOptionsConfig();
  const enabledTypes = Object.keys(optionsConfig).filter(key => optionsConfig[key] !== undefined);

  const selectedOption = optionsConfig[type];
  const alignItems = getAlign(type);

  const { loading, options } = selectedOption;

  return (
    <DrawerTopBox>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: alignItems,
          flexGrow: 1,
        }}
      >
        <Grid size={3}>
          <TypeOptionDropdown enabledTypes={enabledTypes} value={type} onChange={setType} />
        </Grid>
        <ContentObjectOptionsRender
          type={type}
          loading={loading}
          options={options}
          optionsConfig={optionsConfig}
          formState={formState}
          dispatch={dispatch}
        />
      </Grid>
    </DrawerTopBox>
  );
};
